import { FormEvent, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { updateMe } from "../lib/api";

/** Self-service profile editor, shared by both consoles. A user may
 * change only their name, mobile, and picture — email, role and access
 * are managed for them. */
export function EditProfileDrawer({
  open,
  firstName,
  lastName,
  mobile,
  avatarUrl,
  initials,
  onClose,
  onSaved,
}: {
  open: boolean;
  firstName: string;
  lastName: string;
  mobile: string;
  avatarUrl: string | null;
  initials: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [first, setFirst] = useState(firstName);
  const [last, setLast] = useState(lastName);
  const [mob, setMob] = useState(mobile);
  // null = leave avatar as-is; "" = clear it; data-URL = new picture.
  const [avatarChange, setAvatarChange] = useState<string | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);

  const seed = `${firstName}:${lastName}:${mobile}:${avatarUrl ?? ""}`;
  const [seenSeed, setSeenSeed] = useState(seed);
  if (seed !== seenSeed) {
    setSeenSeed(seed);
    setFirst(firstName);
    setLast(lastName);
    setMob(mobile);
    setAvatarChange(null);
    setFileError(null);
  }

  const onFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/^image\/(png|jpe?g|webp)$/.test(file.type)) {
      setFileError("Use a PNG, JPG or WebP image.");
      return;
    }
    if (file.size > 3 * 1024 * 1024) {
      setFileError("Please pick an image under 3 MB.");
      return;
    }
    setFileError(null);
    const reader = new FileReader();
    reader.onload = () => setAvatarChange(String(reader.result));
    reader.readAsDataURL(file);
  };

  const mut = useMutation({
    mutationFn: () => {
      const body: {
        first_name?: string;
        last_name?: string;
        mobile?: string;
        avatar_base64?: string;
      } = {
        first_name: first.trim(),
        last_name: last.trim(),
        mobile: mob.trim(),
      };
      if (avatarChange !== null) body.avatar_base64 = avatarChange;
      return updateMe(body);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["me"] });
      onSaved();
    },
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!first.trim() || !last.trim() || mut.isPending) return;
    mut.mutate();
  };

  const previewUrl =
    avatarChange && avatarChange.length > 0
      ? avatarChange
      : avatarChange === ""
        ? null
        : avatarUrl;

  return (
    <>
      <div
        className={"scrim" + (open ? " open" : "")}
        onClick={onClose}
        aria-hidden="true"
      />
      <aside
        className={"drawer" + (open ? " open" : "")}
        role="dialog"
        aria-modal="true"
      >
        <div className="drawer-head">
          <div>
            <h2>Edit details</h2>
            <p>
              You can change your name, mobile and picture. Email, role and
              access are managed for you.
            </p>
          </div>
          <button className="x" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        <form onSubmit={submit} style={{ display: "contents" }}>
          <div className="drawer-body">
            <span className="step">PROFILE PICTURE</span>
            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              {previewUrl ? (
                <span
                  className="avatar-lg"
                  style={{
                    backgroundImage: `url(${previewUrl})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    color: "transparent",
                  }}
                >
                  {initials}
                </span>
              ) : (
                <span className="avatar-lg">{initials}</span>
              )}
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button
                  type="button"
                  className="btn"
                  onClick={() => fileRef.current?.click()}
                >
                  Choose image
                </button>
                {previewUrl && (
                  <button
                    type="button"
                    className="btn"
                    onClick={() => setAvatarChange("")}
                  >
                    Remove
                  </button>
                )}
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  onChange={onFile}
                  style={{ display: "none" }}
                />
              </div>
            </div>
            {fileError && (
              <span className="tray-cap" style={{ color: "#B00020" }}>
                {fileError}
              </span>
            )}

            <span className="step">NAME</span>
            <div className="grid-2">
              <div className="field">
                <label>First name</label>
                <input
                  value={first}
                  onChange={(e) => setFirst(e.target.value)}
                  autoFocus
                />
              </div>
              <div className="field">
                <label>Last name</label>
                <input value={last} onChange={(e) => setLast(e.target.value)} />
              </div>
            </div>

            <span className="step">CONTACT</span>
            <div className="field">
              <label>
                Mobile<span className="hint">optional</span>
              </label>
              <input
                value={mob}
                onChange={(e) => setMob(e.target.value)}
                placeholder="+91 90000 00000"
              />
            </div>

            {mut.isError && (
              <div
                className="notice"
                style={{ background: "#FDE8E8", borderColor: "#F5B5B5" }}
              >
                <p style={{ color: "#B00020" }}>
                  {(mut.error as Error).message}
                </p>
              </div>
            )}
          </div>
          <div className="drawer-foot">
            <button type="button" className="btn" onClick={onClose}>
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-gold"
              disabled={mut.isPending}
            >
              {mut.isPending ? "Saving…" : "Save details"}
            </button>
          </div>
        </form>
      </aside>
    </>
  );
}
